import React, { useState, useCallback } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { MessageSquare, Pencil, Check, X, Plus, Globe, Sparkles, Trash2, Zap } from "lucide-react";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";
import VariableDropdown from "../FlowBuilder/VariableDropdown";

const emptyApiExpectedVariables = () => ({ required: [], optional: [] });

const normalizeApiExpectedVariables = (raw) => {
  if (!raw || typeof raw !== "object") return emptyApiExpectedVariables();
  const mapEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === "string") {
      const name = entry.trim();
      return name ? { name, description: "" } : null;
    }
    const name = String(entry.name || "").trim();
    if (!name) return null;
    return { name, description: String(entry.description || "").trim() };
  };
  const required = (Array.isArray(raw.required) ? raw.required : []).map(mapEntry).filter(Boolean);
  const optional = (Array.isArray(raw.optional) ? raw.optional : []).map(mapEntry).filter(Boolean);
  const requiredNames = new Set(required.map((v) => v.name));
  return {
    required,
    optional: optional.filter((v) => !requiredNames.has(v.name)),
  };
};

const stripVariableName = (value) =>
  String(value || "")
    .trim()
    .replace(/^\{\{|\}\}$/g, "")
    .trim();

const buildFormState = (data) => ({
  triggerType: data.triggerType || "keyword",
  keywords: data.keywords ? [...data.keywords] : [],
  triggerOnAnyKeyword: data.triggerOnAnyKeyword || false,
  expectedTransferVariables: Array.isArray(data.expectedTransferVariables)
    ? data.expectedTransferVariables.map(stripVariableName).filter(Boolean)
    : [],
  apiExpectedVariables: normalizeApiExpectedVariables(data.apiExpectedVariables),
});

const triggerLabels = {
  keyword: "Keyword",
  welcome: "Welcome Message",
  menu: "Main Menu",
  default: "Default (Fallback)",
  api: "API Activation",
};

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const keywords = data.keywords || [];
  const triggerType = data.triggerType || "keyword";
  const triggerOnAnyKeyword = data.triggerOnAnyKeyword || false;
  const aiHelpOn = data.allowAiHelpDuringConversation === true;
  const expectedVars = (data.expectedTransferVariables || []).filter(Boolean);
  const apiVars = normalizeApiExpectedVariables(data.apiExpectedVariables);
  const apiVarCount = apiVars.required.length + apiVars.optional.length;

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#F3E8FF", padding: "10px", minWidth: "240px", maxWidth: "300px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit trigger"
      >
        <Pencil size={12} className="text-purple-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-purple-100 rounded-lg">
            {triggerType === "api" ? (
              <Zap size={14} className="text-amber-600" />
            ) : (
              <MessageSquare size={14} className="text-purple-600" />
            )}
          </div>
          <span className="text-[12px] font-bold text-purple-900">Flow Trigger</span>
        </div>

        <div className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full inline-block mb-1.5">
          {triggerLabels[triggerType] || triggerType}
        </div>

        {aiHelpOn && (
          <div className="flex items-center gap-1.5 mt-1 mb-1">
            <Sparkles size={10} className="text-indigo-600" />
            <span className="text-[10px] font-semibold text-indigo-700">AI help ON</span>
          </div>
        )}

        {triggerType === "api" && (
          <div className="mt-1 space-y-1">
            <p className="text-[9px] text-amber-700 font-medium">
              Activated via Ping API for a customer phone number
            </p>
            {apiVarCount > 0 && (
              <p className="text-[9px] text-purple-700 font-medium">
                {apiVars.required.length} required · {apiVars.optional.length} optional
              </p>
            )}
          </div>
        )}

        {triggerType === "keyword" && triggerOnAnyKeyword ? (
          <div className="flex items-center gap-1.5 mt-1">
            <Globe size={10} className="text-green-600" />
            <span className="text-[10px] font-semibold text-green-700">Any message</span>
          </div>
        ) : triggerType === "keyword" && keywords.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-1">
            {keywords.map((kw, i) => (
              <span key={i} className="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-medium">
                {kw}
              </span>
            ))}
          </div>
        ) : triggerType === "keyword" ? (
          <p className="text-[10px] text-gray-400 italic">No keywords set</p>
        ) : null}

        {triggerType !== "api" && expectedVars.length > 0 && (
          <p className="text-[9px] text-purple-700 mt-2 font-medium leading-snug">
            Expected: {expectedVars.join(", ")}
          </p>
        )}
      </div>
    </div>
  );
};

const ExpectedVariableRow = ({ index, value, description, flowVariables, onCreateVariable, onChange, onDescriptionChange, onRemove, showDescription = false }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-gray-400 w-5 shrink-0">{index + 1}.</span>
      <VariableDropdown
        value={value}
        onChange={(name) => onChange(name || "")}
        flowVariables={flowVariables || []}
        allowCreate
        onCreateVariable={onCreateVariable}
        placeholder="Select variable..."
        className="flex-1"
      />
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
        title="Remove variable"
      >
        <Trash2 size={14} />
      </button>
    </div>
    {showDescription && (
      <input
        type="text"
        value={description || ""}
        onChange={(e) => onDescriptionChange(e.target.value)}
        placeholder="Description for API docs (optional)"
        className="w-full ml-7 px-2 py-1.5 text-[10px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400 placeholder:text-gray-400"
      />
    )}
  </div>
);

const ApiVariableSection = ({
  title,
  subtitle,
  accentClass,
  items,
  flowVariables,
  onCreateVariable,
  onAdd,
  onChange,
  onDescriptionChange,
  onRemove,
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div>
        <label className={`text-[10px] font-bold uppercase tracking-wider ${accentClass}`}>{title}</label>
        <p className="text-[8px] text-gray-400 mt-0.5">{subtitle}</p>
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="text-[9px] font-bold text-purple-600 hover:text-purple-700 flex items-center gap-0.5"
      >
        <Plus size={9} /> Add
      </button>
    </div>
    {items.length === 0 ? (
      <p className="text-[9px] text-gray-400 italic">None configured</p>
    ) : (
      items.map((item, idx) => (
        <ExpectedVariableRow
          key={idx}
          index={idx}
          value={item.name}
          description={item.description}
          flowVariables={flowVariables}
          onCreateVariable={onCreateVariable}
          onChange={(name) => onChange(idx, name)}
          onDescriptionChange={(desc) => onDescriptionChange(idx, desc)}
          onRemove={() => onRemove(idx)}
          showDescription
        />
      ))
    )}
  </div>
);

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => buildFormState(data));
  const [newKeyword, setNewKeyword] = useState("");

  const addKeyword = () => {
    const trimmed = newKeyword.trim();
    if (!trimmed) return;
    if (!form.keywords.includes(trimmed)) {
      setForm((prev) => ({ ...prev, keywords: [...prev.keywords, trimmed] }));
    }
    setNewKeyword("");
  };

  const removeKeyword = (index) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((_, i) => i !== index) }));
  };

  const addExpectedVariable = () => {
    setForm((prev) => ({
      ...prev,
      expectedTransferVariables: [...prev.expectedTransferVariables, ""],
    }));
  };

  const updateExpectedVariable = (idx, name) => {
    setForm((prev) => ({
      ...prev,
      expectedTransferVariables: prev.expectedTransferVariables.map((v, i) => (i === idx ? name : v)),
    }));
  };

  const removeExpectedVariable = (idx) => {
    setForm((prev) => ({
      ...prev,
      expectedTransferVariables: prev.expectedTransferVariables.filter((_, i) => i !== idx),
    }));
  };

  const addApiVariable = (category) => {
    setForm((prev) => ({
      ...prev,
      apiExpectedVariables: {
        ...prev.apiExpectedVariables,
        [category]: [...prev.apiExpectedVariables[category], { name: "", description: "" }],
      },
    }));
  };

  const updateApiVariable = (category, idx, name) => {
    setForm((prev) => ({
      ...prev,
      apiExpectedVariables: {
        ...prev.apiExpectedVariables,
        [category]: prev.apiExpectedVariables[category].map((item, i) =>
          i === idx ? { ...item, name: name || "" } : item
        ),
      },
    }));
  };

  const updateApiVariableDescription = (category, idx, description) => {
    setForm((prev) => ({
      ...prev,
      apiExpectedVariables: {
        ...prev.apiExpectedVariables,
        [category]: prev.apiExpectedVariables[category].map((item, i) =>
          i === idx ? { ...item, description } : item
        ),
      },
    }));
  };

  const removeApiVariable = (category, idx) => {
    setForm((prev) => ({
      ...prev,
      apiExpectedVariables: {
        ...prev.apiExpectedVariables,
        [category]: prev.apiExpectedVariables[category].filter((_, i) => i !== idx),
      },
    }));
  };

  const handleSaveClick = () => {
    const expectedTransferVariables = [...new Set(
      form.expectedTransferVariables.map(stripVariableName).filter(Boolean)
    )];

    const apiExpectedVariables = normalizeApiExpectedVariables({
      required: (form.apiExpectedVariables.required || []).map((item) => ({
        ...item,
        name: stripVariableName(item.name),
      })),
      optional: (form.apiExpectedVariables.optional || []).map((item) => ({
        ...item,
        name: stripVariableName(item.name),
      })),
    });

    onSave({
      triggerType: form.triggerType,
      keywords: form.triggerOnAnyKeyword ? [] : form.keywords,
      triggerOnAnyKeyword: form.triggerOnAnyKeyword,
      expectedTransferVariables,
      apiExpectedVariables,
    });
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#F3E8FF", borderRadius: "12px", padding: "14px", minWidth: "300px", maxWidth: "360px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-purple-200 rounded-lg">
            <MessageSquare size={14} className="text-purple-700" />
          </div>
          <span className="text-[13px] font-bold text-purple-900">Flow Trigger</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSaveClick}
            className="px-3 py-1.5 text-[11px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="trigger">
        <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
          <div>
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Trigger Type</label>
            <select
              value={form.triggerType}
              onChange={(e) => {
                const nextType = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  triggerType: nextType,
                  keywords: nextType !== "keyword" ? [] : prev.keywords,
                  triggerOnAnyKeyword: nextType !== "keyword" ? false : prev.triggerOnAnyKeyword,
                }));
              }}
              className="w-full px-3 py-2 text-[12px] font-semibold bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400"
            >
              <option value="keyword">Keyword</option>
              <option value="welcome">Welcome Message</option>
              <option value="menu">Main Menu</option>
              <option value="default">Default (Fallback)</option>
              <option value="api">API Activation</option>
            </select>
          </div>

          {form.triggerType === "api" && (
            <div className="space-y-3 pt-1 border-t border-gray-100">
              <div className="px-2 py-2 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <Zap size={12} className="text-amber-600" />
                  <span className="text-[10px] font-bold text-amber-800">API-triggered flow</span>
                </div>
                <p className="text-[8px] text-amber-700 leading-relaxed">
                  This flow can be activated via the Ping Public API for a specific customer phone number.
                  Define required and optional variables below — callers must pass all required variables when activating.
                </p>
              </div>

              <ApiVariableSection
                title="Required Variables"
                subtitle="Must be provided in the activation API request"
                accentClass="text-red-600"
                items={form.apiExpectedVariables.required}
                flowVariables={flowVariables}
                onCreateVariable={onCreateVariable}
                onAdd={() => addApiVariable("required")}
                onChange={(idx, name) => updateApiVariable("required", idx, name)}
                onDescriptionChange={(idx, desc) => updateApiVariableDescription("required", idx, desc)}
                onRemove={(idx) => removeApiVariable("required", idx)}
              />

              <ApiVariableSection
                title="Optional Variables"
                subtitle="May be provided to pre-fill flow variables"
                accentClass="text-blue-600"
                items={form.apiExpectedVariables.optional}
                flowVariables={flowVariables}
                onCreateVariable={onCreateVariable}
                onAdd={() => addApiVariable("optional")}
                onChange={(idx, name) => updateApiVariable("optional", idx, name)}
                onDescriptionChange={(idx, desc) => updateApiVariableDescription("optional", idx, desc)}
                onRemove={(idx) => removeApiVariable("optional", idx)}
              />
            </div>
          )}

          {form.triggerType === "keyword" && (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between px-2 py-2 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center gap-1.5">
                  <Globe size={12} className="text-green-600" />
                  <span className="text-[10px] font-bold text-gray-700">Trigger on any message</span>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, triggerOnAnyKeyword: !prev.triggerOnAnyKeyword }))}
                  className={`w-9 h-5 rounded-full transition-colors relative ${form.triggerOnAnyKeyword ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.triggerOnAnyKeyword ? "left-4" : "left-0.5"}`} />
                </button>
              </div>
              {form.triggerOnAnyKeyword && (
                <p className="text-[9px] text-green-600 font-medium px-1">
                  This flow will start on any incoming message. Specific keyword flows will take priority.
                </p>
              )}

              {!form.triggerOnAnyKeyword && (
                <div>
                  <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Keywords</label>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {form.keywords.map((kw, i) => (
                      <span key={i} className="flex items-center gap-1 text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-medium">
                        {kw}
                        <button type="button" onClick={() => removeKeyword(i)} className="hover:text-red-600">
                          <X size={8} />
                        </button>
                      </span>
                    ))}
                    {form.keywords.length === 0 && (
                      <span className="text-[10px] text-gray-400 italic">No keywords added</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addKeyword()}
                      placeholder="Type keyword..."
                      className="flex-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400 placeholder:text-gray-400"
                    />
                    <button
                      type="button"
                      onClick={addKeyword}
                      disabled={!newKeyword.trim()}
                      className="px-2 py-1.5 text-[10px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-30 flex items-center gap-0.5"
                    >
                      <Plus size={10} /> Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {form.triggerType !== "api" && (
            <div className="space-y-2 pt-1 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Expected Transfer Variables</label>
                <button
                  type="button"
                  onClick={addExpectedVariable}
                  className="text-[9px] font-bold text-purple-600 hover:text-purple-700 flex items-center gap-0.5"
                >
                  <Plus size={9} /> Add
                </button>
              </div>
              <p className="text-[8px] text-gray-400">
                List variables this flow expects when another flow transfers here. Use the copied variables directly (e.g. {`{{city_id}}`}).
              </p>
              {form.expectedTransferVariables.length === 0 ? (
                <p className="text-[9px] text-gray-400 italic">None — add variables if this flow is a transfer destination.</p>
              ) : (
                form.expectedTransferVariables.map((name, idx) => (
                  <ExpectedVariableRow
                    key={idx}
                    index={idx}
                    value={name}
                    flowVariables={flowVariables}
                    onCreateVariable={onCreateVariable}
                    onChange={(v) => updateExpectedVariable(idx, v)}
                    onRemove={() => removeExpectedVariable(idx)}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </NodeEditorTabs>

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={handleSaveClick}
          className="flex-1 py-2 text-[12px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main TriggerNode ─────────────────────────────────
const TriggerNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("trigger");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
          presentation="fullscreen"
        />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default TriggerNode;
